import { clsx } from "clsx";
import type { KeyboardEvent } from "react";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { addYears, inMonth, monthOf, parse } from "../../utils/date";
import { useCalendarContext } from "./context";
import { CalendarView } from "./types";
import { useFocusRequest } from "./useFocusRequest";

const COLUMNS = 3;
/** Years either side of today when no limit bounds the list. */
const SPAN = 100;

/** Scrolls the list, never the page, so `cell` sits in view. */
const reveal = (cell: HTMLElement, center: boolean) => {
  const list = cell.offsetParent as HTMLElement | null;
  if (!list) return;
  const top = cell.offsetTop;
  const bottom = top + cell.offsetHeight;
  if (center) {
    list.scrollTop = top - (list.clientHeight - cell.offsetHeight) / 2;
  } else if (top < list.scrollTop) {
    list.scrollTop = top;
  } else if (bottom > list.scrollTop + list.clientHeight) {
    list.scrollTop = bottom - list.clientHeight;
  }
};

export const Years = () => {
  const { value, today, first, last, cursor, moveTo, showView } =
    useCalendarContext();
  const gridRef = useRef<HTMLDivElement>(null);
  useFocusRequest(gridRef, (cell) => reveal(cell, false));

  const { year: cursorYear, month } = monthOf(cursor);
  const todayYear = parse(today).year;
  const firstYear = parse(first).year;
  const lastYear = parse(last).year;

  const years = useMemo(() => {
    const from = Math.max(firstYear, Math.min(todayYear - SPAN, cursorYear));
    const to = Math.min(lastYear, Math.max(todayYear + SPAN, cursorYear));
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  }, [firstYear, lastYear, todayYear, cursorYear]);

  // Open on the cursor's year, centred.
  useLayoutEffect(() => {
    const cell = gridRef.current?.querySelector<HTMLElement>('[tabindex="0"]');
    if (cell) reveal(cell, true);
    // Only when the list opens.
  }, []);

  const choose = useCallback(
    (year: number) => {
      moveTo(inMonth(cursor, { year, month }), true);
      showView(CalendarView.Months, true);
    },
    [cursor, month, moveTo, showView],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        showView(CalendarView.Days, true);
        return;
      }
      const steps: Record<string, number> = {
        ArrowLeft: -1,
        ArrowRight: 1,
        ArrowUp: -COLUMNS,
        ArrowDown: COLUMNS,
        Home: years[0] - cursorYear,
        End: years[years.length - 1] - cursorYear,
        PageUp: -COLUMNS * 4,
        PageDown: COLUMNS * 4,
      };
      const offset = steps[e.key];
      if (offset === undefined) return;
      e.preventDefault();
      moveTo(addYears(cursor, offset), true);
    },
    [cursor, cursorYear, years, moveTo, showView],
  );

  const selectedYear = value === null ? undefined : parse(value).year;

  return (
    <div className="nuco-calendar__years-wrapper">
      <div
        ref={gridRef}
        className="nuco-calendar__dates nuco-text nuco-text--md nuco-text--medium"
        onKeyDown={onKeyDown}
      >
        {years.map((year) => (
          <button
            type="button"
            key={year}
            className={clsx(
              "nuco-calendar__dateButton nuco-calendar__year",
              year === todayYear && "nuco-calendar__dateButton--today",
              year === selectedYear && "nuco-calendar__dateButton--selected",
            )}
            tabIndex={year === cursorYear ? 0 : -1}
            onClick={() => choose(year)}
          >
            {year}
          </button>
        ))}
      </div>
    </div>
  );
};
